import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addMaterialsEnergyWater41_1aLCA,
  deleteMaterialsEnergyWater41_1aLCA,
  getAllMaterialsEnergyWater41_1aLCA,
  updateMaterialsEnergyWater41_1aLCA,
} from "../../../../../../apis/Product/UseStageFlow41Api";
import {
  getProductMaterialUsage34_1aDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../../apis/Dropdown";
import { REFRESH_USE_STAGE_41_1_TOTALS } from "../../Header";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const MaterialsEnergyWater41_1aTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductMaterialUsage34_1aDropdown({
      setDropdown: setMaterialDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllMaterialsEnergyWater41_1aLCA({
      productId,
      setObjects: (rows) => {
        setData(rows);
        window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_1_TOTALS));
      },
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Convert country codes to names when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          const updates = {};
          
          if (item.country && typeof item.country === "string") {
            if (item.country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.country
              );
              if (foundCountry && item.country !== foundCountry.name) {
                hasChanges = true;
                updates.country = foundCountry.name;
              }
            }
          }
          
          return hasChanges ? { ...item, ...updates } : item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && materialDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.what_is_added === "string"
            ? row.what_is_added
            : row.what_is_added?.value || row.what_is_added?.code;
        if (materialCode) {
          const selected = materialDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === materialCode || opt.value === materialCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitMaterials(newUnits);
    }
  }, [data, materialDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      what_is_added: {
        targetField: "quantity_per_single_use",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          _rowData,
          rowId = "new"
        ) => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitMaterials((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "what_is_added",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialCode =
        typeof editedItem.what_is_added === "string"
          ? editedItem.what_is_added
          : editedItem.what_is_added?.value || editedItem.what_is_added?.code || null;
      
      // Convert country name back to code for backend
      let countryCode = editedItem.country;
      if (countryCode) {
        if (typeof countryCode === "object") {
          countryCode = countryCode.code || countryCode.value || countryCode.name || null;
        } else if (typeof countryCode === "string" && countryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === countryCode || option.value === countryCode
          );
          if (foundCountry) {
            countryCode = foundCountry.code;
          }
        }
      }
      
      updateMaterialsEnergyWater41_1aLCA({
        productId,
        rowId: editedItem.id,
        process_name: editedItem.process_name || null,
        describe_use_cycles: editedItem.describe_use_cycles || null,
        what_is_added: materialCode,
        country: countryCode || null,
        quantity_per_single_use: editedItem.quantity_per_single_use || 0,
        cycles_per_lifetime: editedItem.cycles_per_lifetime || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () =>
          window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_1_TOTALS)),
      });
    },
    [productId, data, countryDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialCode =
        typeof newItem.what_is_added === "string"
          ? newItem.what_is_added
          : newItem.what_is_added?.value || newItem.what_is_added?.code || null;
      
      // Convert country name back to code for backend
      let countryCode = newItem.country;
      if (countryCode) {
        if (typeof countryCode === "object") {
          countryCode = countryCode.code || countryCode.value || countryCode.name || null;
        } else if (typeof countryCode === "string" && countryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === countryCode || option.value === countryCode
          );
          if (foundCountry) {
            countryCode = foundCountry.code;
          }
        }
      }
      
      addMaterialsEnergyWater41_1aLCA({
        productId,
        process_name: newItem.process_name || null,
        describe_use_cycles: newItem.describe_use_cycles || null,
        what_is_added: materialCode,
        country: countryCode || null,
        quantity_per_single_use: newItem.quantity_per_single_use || 0,
        cycles_per_lifetime: newItem.cycles_per_lifetime || 0,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteMaterialsEnergyWater41_1aLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "process_name",
        accessor: "process_name",
        header: `${t("PROCESS_NAME") || "Process name"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "describe_use_cycles",
        accessor: "describe_use_cycles",
        header: t("DESCRIBE_USE_CYCLES") || "Describe use and cycles",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "what_is_added",
        accessor: "what_is_added",
        header: `${t("WHAT_IS_ADDED") || "What is added"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_MATERIAL") || "Select material...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "what_is_added",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "country",
        accessor: "country",
        header: t("COUNTRY") || "Country",
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: "quantity_per_single_use",
        accessor: "quantity_per_single_use",
        header: `${t("QUANTITY_PER_SINGLE_USE") || "Quantity per single use per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "what_is_added",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "cycles_per_lifetime",
        accessor: "cycles_per_lifetime",
        header: `${t("CYCLES_PER_LIFETIME") || "Cycles (Uses) per functional unit during lifetime"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitMaterials,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      what_is_added: materialDropdown || [],
      country: countryDropdown || [],
    }),
    [materialDropdown, countryDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default MaterialsEnergyWater41_1aTable;

